/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import genericMessages from '@modules/project-lib/genericMessages'
import usePermissions from '@modules/permissions-lib/hooks'
import { useSuspendOrganizationProjectsMutation } from '@modules/project-admin-lib/hooks/suspend'
import { addToast } from '@modules/cui/Toasts'

export type Props = {
  organizationId: string
}

const SuspendProjectsButton: React.FunctionComponent<Props> = ({ organizationId }) => {
  const { formatMessage } = useIntl()

  const suspendProjectsMutation = useSuspendOrganizationProjectsMutation()

  const { hasPermissions: hasUpdatePermisson } = usePermissions([
    { type: `organization`, action: 'update', id: organizationId },
  ])

  const [cause, setCause] = useState('')
  const [isConfirming, setIsConfirming] = useState(false)

  return (
    <Fragment>
      {renderButton()}
      {renderModal()}
    </Fragment>
  )

  function renderButton() {
    return (
      <EuiButton color='warning' onClick={openModal} disabled={!hasUpdatePermisson}>
        <FormattedMessage id='project.suspend.button' defaultMessage='Suspend projects' />
      </EuiButton>
    )
  }

  function renderModal() {
    if (!isConfirming) {
      return
    }

    const { isLoading } = suspendProjectsMutation

    return (
      <EuiOverlayMask>
        <EuiModal onClose={closeModal}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <FormattedMessage id='project.suspend.title' defaultMessage='Suspend projects?' />
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            <EuiText>
              <p>
                <FormattedMessage
                  id='project.suspend.body'
                  defaultMessage="This will suspend <strong>all</strong> of this organization's projects. Please provide a reason below."
                  values={{ strong: (content) => <strong>{content}</strong> }}
                />
              </p>
            </EuiText>
            <EuiSpacer size='m' />
            <EuiFieldText
              name='cause-input'
              placeholder={formatMessage({
                id: 'project.suspend.causePlaceholder',
                defaultMessage: 'Reason',
              })}
              value={cause}
              onChange={(e) => setCause(e.target.value)}
            />
          </EuiModalBody>

          <EuiModalFooter>
            {!isLoading && (
              <EuiButtonEmpty onClick={closeModal}>
                <FormattedMessage {...genericMessages.cancel} />
              </EuiButtonEmpty>
            )}

            <EuiButton
              isDisabled={cause.trim().length === 0}
              onClick={() => {
                suspendProjectsMutation.mutate({ organizationId, cause }, { onSuccess, onError })
              }}
              fill={true}
              color='warning'
              isLoading={isLoading}
            >
              {isLoading ? (
                <FormattedMessage {...genericMessages.suspending} />
              ) : (
                <FormattedMessage {...genericMessages.suspend} />
              )}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  function onSuccess() {
    addToast({
      family: 'project.suspend',
      color: 'success',
      title: <FormattedMessage id='project.suspend.success' defaultMessage='Projects suspended' />,
    })
    closeModal()
  }

  function onError() {
    addToast({
      family: 'project.suspend',
      color: 'danger',
      title: <FormattedMessage {...genericMessages.sorryErrorOccurred} />,
    })
    closeModal()
  }

  function openModal() {
    setIsConfirming(true)
  }

  function closeModal() {
    setIsConfirming(false)
    setCause('')
  }
}

export default SuspendProjectsButton
